import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { complianceAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Plus, Check, Warning, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ComplianceCalendarPage() {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const loadData = async () => {
    setLoading(true);
    try {
      const [calendarRes, tasksRes] = await Promise.all([
        complianceAPI.getCalendar(),
        complianceAPI.listTasks({}),
      ]);
      setEvents(calendarRes.data.events || []);
      setTasks(tasksRes.data.tasks || []);
    } catch (error) {
      console.error('Failed to load compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleMarkComplete = async (taskId) => {
    try {
      await complianceAPI.updateTask(taskId, 'completed');
      toast.success('Task marked as complete');
      loadData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date()).slice(0, 10);
  const overdueTasksCount = tasks.filter(t => t.status === 'overdue').length;
  const pendingTasksCount = tasks.filter(t => t.status === 'pending').length;

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <Check size={16} className="text-green-600" />;
      case 'overdue': return <Warning size={16} className="text-[#DC2626]" />;
      default: return <Clock size={16} className="text-amber-500" />;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'gst_filing': return 'bg-blue-100 text-blue-700';
      case 'tds_payment': return 'bg-purple-100 text-purple-700';
      case 'tds_return': return 'bg-indigo-100 text-indigo-700';
      case 'roc_filing': return 'bg-amber-100 text-amber-700';
      case 'board_meeting': return 'bg-emerald-100 text-emerald-700';
      case 'agm': return 'bg-rose-100 text-rose-700';
      case 'egm': return 'bg-orange-100 text-orange-700';
      case 'tax_filing': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div data-testid="compliance-calendar-page">
      <PageHeader 
        title="Compliance Calendar" 
        subtitle="GST, TDS, Board Meetings, AGM/EGM, and ROC Deadlines"
        actions={
          <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="add-task-btn">
            <Plus size={16} className="mr-2" />
            Add Task
          </Button>
        }
      />
      
      <div className="page-content">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="stats-card">
            <p className="stats-card-label">Overdue Tasks</p>
            <p className="stats-card-value text-[#DC2626]">{overdueTasksCount}</p>
          </div>
          <div className="stats-card">
            <p className="stats-card-label">Pending Tasks</p>
            <p className="stats-card-value text-amber-600">{pendingTasksCount}</p>
          </div>
          <div className="stats-card">
            <p className="stats-card-label">Upcoming Events</p>
            <p className="stats-card-value">{upcomingEvents.length}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="col-span-1">
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md"
              />
            </div>
          </div>

          {/* Upcoming Deadlines */}
          <div className="col-span-2">
            <div className="bg-white border border-[#E2E8F0] rounded-lg">
              <div className="p-4 border-b border-[#E2E8F0]">
                <h3 className="font-medium font-['Plus_Jakarta_Sans']">Upcoming Deadlines</h3>
              </div>
              <div className="divide-y divide-[#E2E8F0]">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <div key={i} className="p-4">
                      <div className="skeleton h-5 w-48 mb-2 rounded"></div>
                      <div className="skeleton h-4 w-32 rounded"></div>
                    </div>
                  ))
                ) : upcomingEvents.length > 0 ? (
                  upcomingEvents.map((event, i) => (
                    <div key={i} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#E2E8F0] flex items-center justify-center text-xs font-medium text-[#0F4C81]">
                          {new Date(event.date).getDate()}
                          <br/>
                          {new Date(event.date).toLocaleString('en', { month: 'short' })}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{event.title}</p>
                          {event.description && <p className="text-xs text-[#64748B] mt-0.5">{event.description}</p>}
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${getTypeColor(event.type)}`}>
                            {event.type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-[#64748B]">{formatDate(event.date)}</p>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-[#64748B]">
                    <p>No upcoming deadlines</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="mt-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg">
            <div className="p-4 border-b border-[#E2E8F0]">
              <h3 className="font-medium font-['Plus_Jakarta_Sans']">Compliance Tasks</h3>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Type</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(5)].map((_, j) => (
                        <td key={j}><div className="skeleton h-4 w-24 rounded"></div></td>
                      ))}
                    </tr>
                  ))
                ) : tasks.length > 0 ? (
                  tasks.map((task) => (
                    <tr key={task.id} data-testid={`task-row-${task.id}`}>
                      <td>
                        <div>
                          <p className="font-medium">{task.title}</p>
                          <p className="text-xs text-[#64748B]">{task.description}</p>
                        </div>
                      </td>
                      <td>
                        <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(task.task_type)}`}>
                          {task.task_type?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>{formatDate(task.due_date)}</td>
                      <td>
                        <span className="inline-flex items-center gap-1">
                          {getStatusIcon(task.status)}
                          <span className="text-sm capitalize">{task.status}</span>
                        </span>
                      </td>
                      <td>
                        {task.status !== 'completed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkComplete(task.id)}
                            className="h-7 text-xs"
                          >
                            <Check size={14} className="mr-1" />
                            Mark Complete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-[#64748B]">
                      No compliance tasks found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
